/* Hallmark · genre: application · macrostructure: Personal Dossier · tone: scholarly-utilitarian · anchor hue: heritage green · pre-emit critique: P5 H5 E5 S5 R5 V5 · contrast: pass · responsive: pass */
/**
 * Profile Page
 *
 * Participant account hub for identity, learning records, and account security.
 */

import {
  ArrowLeft,
  BookOpen,
  Calendar,
  CheckCircle2,
  ChevronRight,
  FileText,
  Fingerprint,
  GraduationCap,
  LockKeyhole,
  LogOut,
  MapPin,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
  UserRoundPen,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { ProgramRegistration } from '@lms/shared/types';
import { calculateAge, getParticipantBackground } from '@lms/shared';
import { FacelessAvatar } from '../components/FacelessAvatar';
import { ChangePasswordModal } from '../components/ChangePasswordModal';
import { EditProfileModal } from '../components/EditProfileModal';
import { ChangeEmailModal } from '../components/ChangeEmailModal';
import { ConfirmEmailChangeModal } from '../components/ConfirmEmailChangeModal';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { useEnrollments } from '../hooks/useEnrollments';
import { useRegistrations } from '../hooks/useRegistrations';
import { useLanguage } from '../lib/LanguageContext';
import { useAuthStore } from '../store/auth';
import { getParticipantNip } from '../lib/participant-nip';
import { apiClient } from '../lib/api-client';

export function Profile() {
  const { t, language } = useLanguage();
  const { user, token } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuthStore(state => state.logout);
  const { data: registrationsResponse } = useRegistrations(user?.id);
  const { data: enrollments } = useEnrollments({ userId: user?.id });
  const registrations = registrationsResponse?.items ?? [];
  const participantNip = getParticipantNip(user, registrations);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isSignOutConfirmationOpen, setIsSignOutConfirmationOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [refreshFailed, setRefreshFailed] = useState(false);
  // Link token lives only in memory. Opening a link never submits a mutation.
  const [emailChangeToken, setEmailChangeToken] = useState<string | null>(
    () =>
      new URLSearchParams(location.hash.slice(1)).get('emailChangeToken') ??
      new URLSearchParams(location.search).get('emailChangeToken')
  );
  const [capturedLinkKey, setCapturedLinkKey] = useState(location.key);
  const incomingToken =
    new URLSearchParams(location.hash.slice(1)).get('emailChangeToken') ??
    new URLSearchParams(location.search).get('emailChangeToken');
  // A second link can navigate to this route without remounting the page.
  // Capture it before the URL scrub, but never resurrect a cancelled link.
  if (incomingToken && capturedLinkKey !== location.key) {
    setCapturedLinkKey(location.key);
    setEmailChangeToken(incomingToken);
  }

  useLayoutEffect(() => {
    // Remove credentials from the browser address before passive effects run.
    const browserUrl = new URL(window.location.href);
    const fragment = new URLSearchParams(browserUrl.hash.slice(1));
    if (!browserUrl.searchParams.has('emailChangeToken') && !fragment.has('emailChangeToken'))
      return;
    browserUrl.searchParams.delete('emailChangeToken');
    if (fragment.has('emailChangeToken')) {
      fragment.delete('emailChangeToken');
      browserUrl.hash = fragment.toString();
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${browserUrl.pathname}${browserUrl.search}${browserUrl.hash}`
    );
  }, [location.search, location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fragment = new URLSearchParams(location.hash.slice(1));
    if (!params.has('emailChangeToken') && !fragment.has('emailChangeToken')) return;
    params.delete('emailChangeToken');
    const scrubHash = fragment.has('emailChangeToken');
    fragment.delete('emailChangeToken');
    const search = params.toString();
    const hash = scrubHash ? fragment.toString() : location.hash.slice(1);
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: hash ? `#${hash}` : '',
      },
      { replace: true }
    );
  }, [location.search, location.pathname, location.hash, navigate]);

  useEffect(() => {
    if (!token || !user?.id) return;
    let active = true;
    let sequence = 0;
    const refresh = async () => {
      const request = ++sequence;
      const snapshot = useAuthStore.getState();
      try {
        const fresh = await apiClient.auth.me();
        const current = useAuthStore.getState();
        // Saves, email confirmation, and logout invalidate an older in-flight read.
        if (
          active &&
          request === sequence &&
          current.token === snapshot.token &&
          current.user === snapshot.user &&
          fresh.id === snapshot.user?.id
        ) {
          current.setAuth(snapshot.token!, fresh);
          setRefreshFailed(false);
        }
      } catch {
        if (active && request === sequence && useAuthStore.getState().user === snapshot.user)
          setRefreshFailed(true);
      }
    };
    const focus = () => {
      void refresh();
    };
    void refresh();
    window.addEventListener('focus', focus);
    return () => {
      active = false;
      window.removeEventListener('focus', focus);
    };
  }, [token, user?.id]);

  const activePrograms =
    enrollments?.filter(enrollment => enrollment.status === 'ACTIVE').length || 0;
  const completedPrograms =
    enrollments?.filter(enrollment => enrollment.status === 'COMPLETED').length || 0;
  const totalPrograms = enrollments?.length || 0;
  const totalMaterialsCompleted =
    enrollments?.reduce((sum, enrollment) => sum + (enrollment.completions?.length || 0), 0) || 0;
  const avgCompletion =
    totalPrograms > 0
      ? Math.round(
          enrollments!.reduce((sum, enrollment) => sum + enrollment.progress, 0) / totalPrograms
        )
      : 0;

  const completedProfileFields = [
    user?.name,
    user?.email,
    user?.phone,
    user?.gender,
    user?.dateOfBirth,
  ].filter(Boolean).length;
  const participantBackground = getParticipantBackground(user?.legacyMetadata);
  const metadataDomisili = user?.legacyMetadata?.domisili;
  const domisili = user?.domisili ?? (typeof metadataDomisili === 'string' ? metadataDomisili : '');
  const completedBackgroundFields = [
    participantBackground.occupation,
    participantBackground.educationLevel,
  ].filter(Boolean).length;
  const profileFieldTotal = 7;
  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'id-ID', {
        month: 'long',
        year: 'numeric',
      })
    : t(copy.profile.recently);

  const displayGender =
    user?.gender === 'FEMALE'
      ? t(copy.profile.genderFemale)
      : user?.gender === 'MALE'
        ? t(copy.profile.genderMale)
        : t(copy.common.notSet);
  const participantAge = user?.dateOfBirth ? calculateAge(user.dateOfBirth) : null;
  const occupationValue = participantBackground.occupation
    ? participantBackground.occupation === 'OTHER'
      ? participantBackground.occupationOther || t(copy.common.notSet)
      : t(copy.participantBackground.options[participantBackground.occupation])
    : t(copy.common.notSet);
  const educationValue = participantBackground.educationLevel
    ? participantBackground.educationLevel === 'OTHER'
      ? participantBackground.educationOther || t(copy.common.notSet)
      : t(copy.participantBackground.options[participantBackground.educationLevel])
    : t(copy.common.notSet);

  return (
    <div className="max-w-6xl space-y-8 pb-12 animate-fadeIn">
      {statusMessage && (
        <p role="status" className="rounded-2xl bg-primary/10 p-4 text-sm text-primary">
          {statusMessage}
        </p>
      )}
      {refreshFailed && (
        <p
          role="status"
          className="rounded-2xl bg-surface-container-high p-4 text-sm text-on-surface-variant"
        >
          {t(copy.profile.refreshError)}
        </p>
      )}
      <section className="overflow-hidden rounded-[2rem] bg-surface-container-low p-5 sm:p-8 lg:p-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-label font-bold text-primary no-underline transition-colors hover:text-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
        >
          <ArrowLeft size={15} aria-hidden="true" />
          <Bilingual {...copy.profile.backToDashboard} />
        </Link>

        <div className="mt-8 grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="min-w-0 lg:col-span-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-[1.75rem] bg-primary text-on-primary shadow-level2 sm:size-28">
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="size-full object-cover" />
                ) : (
                  <FacelessAvatar className="size-14" />
                )}
              </div>
              <div className="min-w-0 space-y-3">
                <div>
                  <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                    <Bilingual {...copy.profile.accountOverview} />
                  </p>
                  <h1 className="m-0 mt-2 min-w-0 break-words [overflow-wrap:anywhere] text-3xl font-extrabold leading-tight tracking-tight text-on-surface sm:text-4xl">
                    {user?.name || t(copy.profile.title)}
                  </h1>
                </div>
                <p className="m-0 max-w-2xl text-sm leading-relaxed text-on-surface-variant">
                  <Bilingual {...copy.profile.subtitle} />
                </p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-on-surface-variant">
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar size={14} aria-hidden="true" />
                    <Bilingual {...copy.profile.memberSince} /> {memberSince}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-primary" aria-hidden="true" />
                    <Bilingual {...copy.profile.accountReady} />
                  </span>
                  {participantNip && (
                    <span className="inline-flex items-center gap-1.5 font-mono font-bold text-primary">
                      <Fingerprint size={14} aria-hidden="true" />
                      <Bilingual {...copy.profile.participantNipShort} /> {participantNip}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[1.5rem] bg-primary p-5 text-on-primary shadow-level2 lg:col-span-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.14em] text-on-primary/70">
                  <Bilingual {...copy.profile.profileCompletion} />
                </p>
                <p className="mt-2 text-2xl font-extrabold">
                  {completedProfileFields + completedBackgroundFields}/{profileFieldTotal}
                </p>
              </div>
              <CheckCircle2 size={22} className="text-secondary" aria-hidden="true" />
            </div>
            <p className="mb-4 mt-1 text-xs leading-relaxed text-on-primary/80">
              <Bilingual {...copy.profile.profileCompletionDesc} />
            </p>
            <button
              type="button"
              onClick={() => setIsEditModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg bg-secondary px-4 py-2.5 text-xs font-bold text-on-secondary transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary active:scale-95"
            >
              <UserRoundPen size={15} aria-hidden="true" />
              <Bilingual {...copy.profile.editProfile} />
            </button>
          </div>
        </div>
      </section>

      <nav
        aria-label={t(copy.profile.profileNavigation)}
        className="grid grid-cols-1 gap-2 rounded-2xl bg-surface-container-low p-2 sm:grid-cols-3"
      >
        {[
          { href: '#identity', label: copy.profile.profileIdentity, icon: UserRound },
          { href: '#learning', label: copy.profile.profileLearning, icon: GraduationCap },
          { href: '#account', label: copy.profile.profileAccount, icon: LockKeyhole },
        ].map(({ href, label, icon: Icon }) => (
          <a
            key={href}
            href={href}
            className="inline-flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-bold text-on-surface no-underline transition-colors hover:bg-surface-container-lowest hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
          >
            <span className="inline-flex items-center gap-2">
              <Icon size={17} className="text-primary" aria-hidden="true" />
              <Bilingual {...label} />
            </span>
            <ChevronRight size={16} className="text-on-surface-variant" aria-hidden="true" />
          </a>
        ))}
      </nav>

      <div className="grid gap-8 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-7">
          <section
            id="identity"
            className="rounded-[2rem] bg-surface-container-lowest p-5 shadow-level1 sm:p-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                  <Bilingual {...copy.profile.personalInfo} />
                </p>
                <h2 className="m-0 mt-2 text-2xl font-extrabold text-on-surface">
                  <Bilingual {...copy.profile.contactInfo} />
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-surface-container-low px-4 py-2.5 text-xs font-bold text-primary transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
              >
                <Bilingual {...copy.profile.manageDetails} />
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                ...(participantNip
                  ? [
                      {
                        label: copy.profile.participantNip,
                        value: participantNip,
                        icon: Fingerprint,
                        monospace: true,
                      },
                    ]
                  : []),
                { label: copy.profile.email, value: user?.email || t(copy.common.na), icon: Mail },
                {
                  label: copy.profile.phone,
                  value: user?.phone || t(copy.common.notSet),
                  icon: Phone,
                },
                { label: copy.profile.gender, value: displayGender, icon: UserRound },
                {
                  label: copy.profile.domisili,
                  value: domisili || t(copy.common.notSet),
                  icon: MapPin,
                },
                {
                  label: copy.profile.dateOfBirth,
                  value: user?.dateOfBirth
                    ? `${new Date(user.dateOfBirth).toLocaleDateString(
                        language === 'ar' ? 'ar-SA' : 'id-ID'
                      )}${
                        participantAge !== null
                          ? ` · ${t(copy.profile.ageSummary).replace(
                              '{age}',
                              String(participantAge)
                            )}`
                          : ''
                      }`
                    : t(copy.common.notSet),
                  icon: Calendar,
                },
                {
                  label: copy.participantBackground.occupationLabel,
                  value: occupationValue,
                  icon: UserRound,
                },
                {
                  label: copy.participantBackground.educationLabel,
                  value: educationValue,
                  icon: GraduationCap,
                },
              ].map(({ label, value, icon: Icon, monospace = false }) => (
                <div
                  key={label.id}
                  className="flex min-w-0 items-start gap-3 rounded-2xl bg-surface-container-low p-4"
                >
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Icon size={18} aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <p className="m-0 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      <Bilingual {...label} />
                    </p>
                    <p
                      className={`mt-1 break-words text-sm font-bold text-on-surface ${monospace ? 'font-mono tracking-wide text-primary' : ''}`}
                    >
                      {value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[2rem] bg-surface-container-lowest p-5 shadow-level1 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
                  <Bilingual {...copy.profile.myRegistrations} />
                </p>
                <h2 className="m-0 mt-2 text-2xl font-extrabold text-on-surface">
                  <Bilingual {...copy.profile.registrationRecords} />
                </h2>
              </div>
              <Link
                to="/programs"
                className="inline-flex items-center gap-2 rounded-lg bg-surface-container-low px-4 py-2.5 text-xs font-bold text-primary no-underline transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
              >
                <Bilingual {...copy.profile.viewPrograms} />
                <ArrowLeft size={14} className="rotate-180" aria-hidden="true" />
              </Link>
            </div>

            <div className="mt-6 space-y-3">
              {registrations.length > 0 ? (
                registrations.slice(0, 5).map((registration: ProgramRegistration) => (
                  <div
                    key={registration.id}
                    className="flex flex-col gap-4 rounded-2xl bg-surface-container-low p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="m-0 truncate text-sm font-bold text-on-surface">
                        {registration.program?.name || t(copy.common.programFallback)}
                      </p>
                      <p className="mt-1 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                        {t(
                          copy.common.registrationStatus[
                            registration.status as keyof typeof copy.common.registrationStatus
                          ] || { id: registration.status, ar: registration.status }
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Link
                        to={`/programs/${registration.programId}`}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-primary no-underline transition-colors hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                      >
                        <Bilingual {...copy.profile.openProgram} />
                        <ChevronRight size={14} aria-hidden="true" />
                      </Link>
                      {registration.status === 'APPROVED' && (
                        <Link
                          to={`/receipt/${registration.id}`}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-surface-container-lowest px-3 py-2 text-xs font-bold text-primary no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                        >
                          <FileText size={14} aria-hidden="true" />
                          <Bilingual {...copy.profile.receipt} />
                        </Link>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl bg-surface-container-low p-6">
                  <p className="m-0 text-sm leading-relaxed text-on-surface-variant">
                    <Bilingual {...copy.profile.noRegistrations} />
                  </p>
                  <Link
                    to="/programs"
                    className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-primary no-underline hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    <Bilingual {...copy.profile.browsePrograms} />
                    <ChevronRight size={14} aria-hidden="true" />
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="space-y-8 lg:col-span-5">
          <section id="learning" className="rounded-[2rem] bg-surface-container-low p-5 sm:p-8">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              <Bilingual {...copy.profile.learningStats} />
            </p>
            <h2 className="m-0 mt-2 text-2xl font-extrabold text-on-surface">
              <Bilingual {...copy.profile.learningOverview} />
            </h2>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                {
                  to: '/programs',
                  value: activePrograms,
                  label: copy.profile.activePrograms,
                  icon: GraduationCap,
                  tone: 'text-primary',
                },
                {
                  to: '/materials',
                  value: totalMaterialsCompleted,
                  label: copy.profile.materialsCompleted,
                  icon: BookOpen,
                  tone: 'text-on-secondary-container',
                },
                {
                  to: '/results',
                  value: `${completedPrograms}/${totalPrograms}`,
                  label: copy.profile.programsCompleted,
                  icon: CheckCircle2,
                  tone: 'text-success',
                },
                {
                  to: '/programs',
                  value: `${avgCompletion}%`,
                  label: copy.profile.avgCompletion,
                  icon: GraduationCap,
                  tone: 'text-primary',
                },
              ].map(({ to, value, label, icon: Icon, tone }) => (
                <Link
                  key={label.id}
                  to={to}
                  className="group rounded-2xl bg-surface-container-lowest p-4 text-on-surface no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                >
                  <Icon size={18} className={tone} aria-hidden="true" />
                  <p className={`mb-1 mt-5 text-2xl font-extrabold ${tone}`}>{value}</p>
                  <p className="m-0 text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...label} />
                  </p>
                </Link>
              ))}
            </div>
          </section>

          <section
            id="account"
            className="rounded-[2rem] bg-surface-container-lowest p-5 shadow-level1 sm:p-8"
          >
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              <Bilingual {...copy.profile.accountSettings} />
            </p>
            <h2 className="m-0 mt-2 text-2xl font-extrabold text-on-surface">
              <Bilingual {...copy.profile.accountAndSecurity} />
            </h2>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(true)}
                className="flex w-full items-center justify-between gap-4 rounded-2xl bg-surface-container-low p-4 text-left text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] rtl:text-right"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <UserRoundPen size={18} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold">
                      <Bilingual {...copy.profile.dataProfile} />
                    </span>
                    <span className="mt-1 block text-xs text-on-surface-variant">
                      <Bilingual {...copy.profile.dataProfileDesc} />
                    </span>
                  </span>
                </span>
                <ChevronRight size={18} className="shrink-0 text-primary" aria-hidden="true" />
              </button>

              <button
                type="button"
                onClick={() => setIsPasswordModalOpen(true)}
                className="flex w-full items-center justify-between gap-4 rounded-2xl bg-surface-container-low p-4 text-left text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] rtl:text-right"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary-container text-on-secondary-container">
                    <LockKeyhole size={18} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold">
                      <Bilingual {...copy.profile.changePassword} />
                    </span>
                    <span className="mt-1 block text-xs text-on-surface-variant">
                      <Bilingual {...copy.profile.securityDesc} />
                    </span>
                  </span>
                </span>
                <ChevronRight size={18} className="shrink-0 text-primary" aria-hidden="true" />
              </button>

              <button
                type="button"
                onClick={() => setIsEmailModalOpen(true)}
                className="flex w-full items-center justify-between gap-4 rounded-2xl bg-surface-container-low p-4 text-left text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 duration-150 rtl:text-right"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Mail size={18} aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold">
                      <Bilingual {...copy.profile.changeEmail} />
                    </span>
                    <span className="mt-1 block text-xs text-on-surface-variant">
                      <Bilingual {...copy.profile.changeEmailDescription} />
                    </span>
                  </span>
                </span>
                <ChevronRight size={18} className="shrink-0 text-primary" aria-hidden="true" />
              </button>

              {isSignOutConfirmationOpen ? (
                <div className="rounded-2xl bg-error-container p-4">
                  <p className="m-0 text-sm font-bold text-on-error-container">
                    <Bilingual {...copy.profile.confirmSignOut} />
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setIsSignOutConfirmationOpen(false)}
                      className="rounded-lg bg-surface-container-lowest px-4 py-2.5 text-xs font-bold text-on-surface transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                    >
                      <Bilingual {...copy.common.cancel} />
                    </button>
                    <button
                      type="button"
                      onClick={logout}
                      className="rounded-lg bg-error px-4 py-2.5 text-xs font-bold text-on-error transition-colors hover:bg-error/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error active:scale-95"
                    >
                      <Bilingual {...copy.profile.signOut} />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsSignOutConfirmationOpen(true)}
                  className="flex w-full items-center justify-between gap-4 rounded-2xl bg-surface-container-low p-4 text-left text-error transition-colors hover:bg-error-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-error active:scale-[0.99] rtl:text-right"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-error-container text-error">
                      <LogOut size={18} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold">
                        <Bilingual {...copy.profile.signOut} />
                      </span>
                      <span className="mt-1 block text-xs text-on-surface-variant">
                        <Bilingual {...copy.profile.signOutDesc} />
                      </span>
                    </span>
                  </span>
                  <ChevronRight size={18} className="shrink-0" aria-hidden="true" />
                </button>
              )}
            </div>
          </section>
        </div>
      </div>

      {isEditModalOpen && (
        <EditProfileModal
          onClose={() => setIsEditModalOpen(false)}
          onSaved={() => {
            setStatusMessage(t(copy.profile.successMsg));
            setRefreshFailed(false);
          }}
        />
      )}
      {isEmailModalOpen && <ChangeEmailModal onClose={() => setIsEmailModalOpen(false)} />}
      {emailChangeToken && (
        <ConfirmEmailChangeModal
          token={emailChangeToken}
          onClose={() => setEmailChangeToken(null)}
          onConfirmed={(oldEmail, newEmail) => {
            setRefreshFailed(false);
            setStatusMessage(
              t(copy.profile.emailChanged)
                .replace('{oldEmail}', oldEmail)
                .replace('{newEmail}', newEmail)
            );
          }}
        />
      )}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}
